/**
 * "Facebook didn't take this post at 9:53 PM: the connection timed out.
 * Nothing was posted. “Game 4 tonight…”" — the Reach calendar's words, plus
 * the post's opening so a Today card says which one.
 */
export function missedNote(
  channel: string,
  at: Date | null,
  failure: string | null | undefined,
  text: string,
  now = new Date()
) {
  const day = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });
  const whenText = at
    ? at.toDateString() === now.toDateString()
      ? ` at ${clock(at)}`
      : ` on ${day(at)} at ${clock(at)}`
    : '';
  const excerpt = text.length > 80 ? `${text.slice(0, 80).trimEnd()}…` : text;
  return (
    `${channel} didn’t take this post${whenText}${failure ? `: ${failure}` : ''}. Nothing was posted.` +
    (excerpt ? ` “${excerpt}”` : '')
  );
}

const clock = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const monthDay = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function andList(xs: string[], last = ' and ') {
  return xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')}${last}${xs[xs.length - 1]}`;
}

/**
 * How late posting now would be ("12 min late", "about 2h late", "3 days
 * late"): hours within a day, then calendar days, as the person counts them.
 */
export function lateBy(at: Date, now = new Date()): string {
  const min = (now.getTime() - at.getTime()) / 60000;
  if (min < 60) return `${Math.max(1, Math.round(min))} min late`;
  if (min < 24 * 60) return `about ${Math.round(min / 60)}h late`;
  const days = Math.max(1, Math.round((startOfDay(now) - startOfDay(at)) / 86400000));
  return `${days} day${days === 1 ? '' : 's'} late`;
}

/** The missed post's button: "Try again" within the hour, else how late it would go out. */
export function retryLabel(at: Date | null, now = new Date()): string {
  if (!at || now.getTime() - at.getTime() < 60 * 60000) return 'Try again';
  return `Post now, ${lateBy(at, now)}`;
}

/**
 * Missed posts on the same channel that failed for the same reason, together
 * (Prototype v7: "2 LinkedIn posts missed their time"). In the order given;
 * a post alone stays alone.
 */
export function groupMissed<
  T extends { platform?: string | null; socialProfileId?: string | null; failure?: string | null },
>(posts: T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const p of posts) {
    const k = `${p.socialProfileId || (p.platform || '').toLowerCase()}|${p.failure || ''}`;
    groups.set(k, [...(groups.get(k) || []), p]);
  }
  return [...groups.values()];
}

// The server's reasons (reach/services/review.js failureReason) are about one
// post; said once for several.
const PLURAL_CAUSE: Record<string, (channel: string) => string> = {
  'it wasn’t sent before its time passed': () => 'they weren’t sent before their time passed',
  'its channel is paused or disconnected, so it wasn’t sent': (c) =>
    `${c} is paused or disconnected, so they weren’t sent`,
  'its connection needs to be renewed in Reach → Channels': (c) =>
    `the ${c} connection needs to be renewed in Reach → Channels`,
  'too many posts went to it at once, so it was asked to wait': (c) =>
    `too many posts went to ${c} at once, so it asked them to wait`,
  'it looked the same as an earlier post': () => 'they looked the same as earlier posts',
  'it couldn’t take the image or video': (c) => `${c} couldn’t take their images or videos`,
  'the text is too long for it': (c) => `the text is too long for ${c}`,
  'it returned an error': (c) => `${c} returned an error`,
};

export function sharedCause(failure: string, channel: string): string {
  return PLURAL_CAUSE[failure]?.(channel) || failure;
}

/** "Oct 14", "Oct 14–15", "Sep 30 – Oct 2"; null when every time is today. */
export function dayRange(times: Date[], now = new Date()): string | null {
  const days = [...new Set(times.map(startOfDay))].sort((a, b) => a - b).map((t) => new Date(t));
  if (!days.length || (days.length === 1 && days[0].getTime() === startOfDay(now))) return null;
  const first = days[0];
  const last = days[days.length - 1];
  if (first.getTime() === last.getTime()) return monthDay(first);
  if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear())
    return `${monthDay(first)}–${last.getDate()}`;
  return `${monthDay(first)} – ${monthDay(last)}`;
}

/** "at 8:30 AM and 9:00 AM", or "on Oct 14 at 8:30 PM and 8:45 PM, and on Oct 15 at 8:30 PM". */
export function missedTimes(times: Date[], now = new Date()): string {
  const byDay = new Map<number, Date[]>();
  for (const t of [...times].sort((a, b) => a.getTime() - b.getTime())) {
    byDay.set(startOfDay(t), [...(byDay.get(startOfDay(t)) || []), t]);
  }
  const today = startOfDay(now);
  if (byDay.size === 1 && byDay.has(today)) return `at ${andList(byDay.get(today)!.map(clock))}`;
  const parts = [...byDay].map(
    ([day, ts]) =>
      `${day === today ? 'today' : `on ${monthDay(ts[0])}`} at ${andList(ts.map(clock))}`
  );
  return andList(parts, ', and ');
}

/** A Today card for several missed posts on one channel, for one reason. */
export function missedGroupWords(
  channel: string,
  times: Date[],
  failure: string | null | undefined,
  now = new Date()
) {
  const n = times.length;
  const range = dayRange(times, now);
  const newest = times.reduce((a, b) => (b > a ? b : a), times[0]);
  const went = `nothing went out ${missedTimes(times, now)}`;
  return {
    preview: `${n} ${channel} posts`,
    title: `${n} ${channel} posts missed their time${range ? `, ${range}` : ''}`,
    corner: range || 'Today',
    note: failure
      ? `Same cause for ${n === 2 ? 'both' : `all ${n}`}: ${sharedCause(failure, channel)}, so ${went}.`
      : `${went[0].toUpperCase()}${went.slice(1)}.`,
    cta: newest ? `Post now, ${lateBy(newest, now)}` : 'Post now',
    skip: 'Skip all',
    posted: `Posting ${n} now. They go out within a few minutes.`,
    skipped: `Skipped ${n}. They won’t be posted.`,
  };
}
